import React, { useCallback, useEffect, useState } from 'react';
import {
  Form, Input, Select, Button, Space, Tag, Typography, Drawer, DatePicker, Spin, message, Popconfirm, Checkbox, Card, Collapse,
  Modal, Switch, Segmented, Table,
} from 'antd';
import { Link } from 'react-router-dom';
import { PlusOutlined, MinusCircleOutlined, LinkOutlined, DeleteOutlined, LockOutlined, InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { formatTS } from '../utils/time';
import { selectFilter } from '../utils/selectFilter';
import LinksFormList, { cleanLinks } from './LinksFormList';
import CommentBox, { CommentAttachments } from './CommentBox';

// ON SCREEN THE LEVELS ARE SWAPPED: a "project" in the code and API (table
// Projects) is shown as a Folder, a "folder" (ProjectFolders) as a Project.
// Code names stay; only visible words use the new ones.
//
// Shared by Oversight > Projects, the folder page and Project Tasks: status
// colors, links, the members editor, comments and the task drawer. See
// routes/projects.py for who sees and does what.

export const PROJECT_STATUSES = ['Active', 'On Hold', 'Done', 'Archived'];
export const PROJECT_STATUS_COLOR = { Active: 'green', 'On Hold': 'orange', Done: 'blue', Archived: 'default' };
export const TASK_STATUSES = ['Open', 'In Progress', 'Done'];
export const TASK_STATUS_COLOR = { Open: 'default', 'In Progress': 'processing', Done: 'success' };
export const PRIORITIES = ['Low', 'Normal', 'High'];
export const PRIORITY_COLOR = { Low: 'default', Normal: 'blue', High: 'red' };

// A Project's page (code: a folder, inside a project shown as a Folder).
export const projectPath = (folderId, projectId) => `/folders/${folderId}/projects/${projectId}`;

// The delete icon at the start of a task row, for those who may delete it
// (canDelete(task)); asks first. Clicks stay off the row (which opens the task).
export const taskDeleteColumn = (canDelete, onDeleted) => ({
  title: '', key: 'delete', width: 44,
  render: (_, t) => (canDelete(t) ? (
    <span onClick={(e) => e.stopPropagation()} role="presentation">
      <Popconfirm title="Delete this task?" description={t.Title} okText="Delete" okButtonProps={{ danger: true }}
        onConfirm={() => api.delete(`/api/project-tasks/${t.ID}`)
          .then(() => { message.success('Task deleted.'); onDeleted?.(); })
          .catch((err) => message.error(err.response?.data?.error || 'Delete failed.'))}>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Delete task" />
      </Popconfirm>
    </span>
  ) : null),
});

// Repeating tasks: marking one Done creates the next (routes/projects.py
// _repeat_task), due the previous date moved on until it is today or later.
export const REPEAT_OPTIONS = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekdays', label: 'Weekdays (Mon–Fri)' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
];
// The task lists' Repeats column: the cycle, or a dash.
export const repeatColumn = {
  title: 'Repeats', dataIndex: 'Repeat', key: 'Repeat', width: 150,
  sorter: (a, b) => (a.Repeat || '').localeCompare(b.Repeat || ''),
  render: (v) => (v ? (
    <span style={{ whiteSpace: 'nowrap' }}>
      <SyncOutlined style={{ color: '#1677ff', marginRight: 6 }} />
      {REPEAT_OPTIONS.find((o) => o.value === v)?.label || v}
    </span>
  ) : <Typography.Text type="secondary">—</Typography.Text>),
};

// The task table, as on a Folder's project cards and a Project's page.
export const taskColumns = () => [
  { title: 'Task', dataIndex: 'Title', key: 'Title',
    render: (v, t) => <span style={{ textDecoration: t.Status === 'Done' ? 'line-through' : undefined }}>{v}</span> },
  { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 170,
    render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
  { title: 'Due', dataIndex: 'DueDate', key: 'DueDate', width: 110,
    render: (v, t) => (v ? <span style={{ color: t.Status !== 'Done' && v < dayjs().format('YYYY-MM-DD') ? '#ff4d4f' : undefined }}>{v}</span> : '—') },
  repeatColumn,
  { title: 'Priority', dataIndex: 'Priority', key: 'Priority', width: 90, render: (v) => <Tag color={PRIORITY_COLOR[v]}>{v}</Tag> },
  { title: 'Status', dataIndex: 'Status', key: 'Status', width: 110, render: (v) => <Tag color={TASK_STATUS_COLOR[v]}>{v}</Tag> },
  { title: 'Applies To', key: 'AppliesTo', width: 280, render: (_, t) => <AppliesToCell task={t} /> },
];

// Archiving a Folder or Project: only once none of its tasks is open.
export const hasOpenTasks = (projects) => projects.some((f) => (f.Tasks || []).some((t) => t.Status !== 'Done'));
export const archiveBlocked = () => Modal.warning({ title: 'Open tasks exist', content: 'Close or delete tasks before archiving.' });

// Add / Edit Project (code: a folder) in Folder folderId. project: the one
// being edited, or null to add. onSaved() after a save.
export function ProjectEditModal({ open, folderId, project, options, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(project ? {
      name: project.Name, description: project.Description || '', is_archived: project.IsArchived,
      members: membersToForm(project.Members), links: linksToForm(project.Links),
      type_id: project.TypeID ?? undefined, campaign_ids: (project.Campaigns || []).map((c) => c.ID),
      dates: [project.StartDate ? dayjs(project.StartDate) : null, project.EndDate ? dayjs(project.EndDate) : null],
    } : { members: [], links: [], campaign_ids: [] });
  }, [open, project]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    if (project && v.is_archived && !project.IsArchived && hasOpenTasks([project])) { archiveBlocked(); return; }
    const body = {
      name: v.name, description: v.description || '', members: v.members || [], links: linksFromForm(v.links),
      type_id: v.type_id ?? null, campaign_ids: v.campaign_ids || [],
      start_date: v.dates?.[0]?.format('YYYY-MM-DD') || null, end_date: v.dates?.[1]?.format('YYYY-MM-DD') || null,
    };
    setSaving(true);
    try {
      if (project) await api.put(`/api/project-folders/${project.ID}`, { ...body, is_archived: !!v.is_archived });
      else await api.post(`/api/projects/${folderId}/folders`, body);
      message.success('Project saved.');
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal maskClosable={false} title={project ? 'Edit Project' : 'Add Project'} open={open}
      onOk={save} onCancel={onClose} confirmLoading={saving} okText="Save" width={680} destroyOnClose>
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
          <Input maxLength={200} placeholder="e.g. 2026-10 Monitor Request" />
        </Form.Item>
        <Form.Item name="description" label="Description"><Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} /></Form.Item>
        <Space wrap size={12}>
          <Form.Item name="type_id" label="Type">
            <Select allowClear showSearch filterOption={selectFilter} style={{ width: 240 }} placeholder="No type"
              options={typeOptions(options, project?.TypeID, project?.TypeName)} />
          </Form.Item>
          {/* For information only: nothing else reads them. */}
          <Form.Item name="dates" label="Dates"><DatePicker.RangePicker allowEmpty={[true, true]} /></Form.Item>
        </Space>
        {/* A project's own campaigns; when it has none, its folder's apply. */}
        <Form.Item name="campaign_ids" label="Campaigns" extra="Leave empty to use the folder's campaigns.">
          <Select mode="multiple" showSearch filterOption={selectFilter} options={campaignOptions(options, project?.Campaigns || [])} />
        </Form.Item>
        <Form.Item label="Project members (see only this project)">
          <MembersFormList name="members" options={options} withRole={false} />
        </Form.Item>
        <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
        {project && (
          <Form.Item name="is_archived" label="Archived" valuePropName="checked"><Switch /></Form.Item>
        )}
      </Form>
    </Modal>
  );
}

// Notify When Done: people who can see the Project (code: folder), as the
// server allows; current picks stay listed by name if they have since lost
// access.
export function useFolderPeople(projectId) {
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!projectId) { setPeople([]); return; }
    api.get(`/api/project-folders/${projectId}/people`).then((r) => setPeople(r.data || [])).catch(() => setPeople([]));
  }, [projectId]);
  return people;
}
function NotifyField({ people, current = [] }) {
  const opts = [
    ...people.map((u) => ({ value: u.ID, label: u.DisplayName })),
    ...current.filter((c) => !people.some((u) => u.ID === c.ID)).map((c) => ({ value: c.ID, label: c.DisplayName || `#${c.ID}` })),
  ];
  return (
    <Form.Item name="notify_when_done" label="Notify When Done"
      extra="They get an alert when this task is marked Done.">
      <Select mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="Nobody" options={opts} />
    </Form.Item>
  );
}

// Add Task to Project projectId (code: folder) of Folder folderId.
// campaignIds: the task's campaign scope (the Project's, else the Folder's).
// initial: form values to start from (a record page's Create Task). onSaved
// gets the new task's ID.
export function AddTaskModal({ open, folderId, projectId, campaignIds, options, initial, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const people = useFolderPeople(open ? projectId : null);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ status: 'Open', priority: 'Normal', links: [], ...appliesToForm(null), ...(initial || {}) });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/projects/${folderId}/tasks`, {
        folder_id: projectId, title: v.title, notes: v.notes || '', assigned_to: v.assigned_to || [],
        due_date: v.due_date ? v.due_date.format('YYYY-MM-DD') : null, status: v.status, priority: v.priority,
        repeat: v.repeat || null,
        links: linksFromForm(v.links), ...appliesFromForm(v), notify_when_done: v.notify_when_done || [],
      });
      message.success('Task added.');
      onSaved?.(res.data?.id);
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal maskClosable={false} title="Add Task" open={open} onOk={save} onCancel={onClose}
      confirmLoading={saving} okText="Add" width={680} destroyOnClose>
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item name="title" label="Task" rules={[{ required: true, message: 'A task needs a title' }]}><Input maxLength={300} /></Form.Item>
        <Form.Item name="notes" label="Notes"><Input.TextArea autoSize={{ minRows: 2, maxRows: 10 }} /></Form.Item>
        <Space wrap size={12}>
          <Form.Item name="assigned_to" label="Assigned To">
            <Select mode="multiple" maxCount={MAX_ASSIGNEES} allowClear showSearch filterOption={selectFilter}
              style={{ minWidth: 240, maxWidth: 480 }} placeholder="Unassigned" options={assigneeOptions(options.users)} />
          </Form.Item>
          <Form.Item name="due_date" label="Due"><DatePicker /></Form.Item>
          <Form.Item name="repeat" label="Repeat"><Select allowClear placeholder="Does not repeat" style={{ width: 160 }} options={REPEAT_OPTIONS} /></Form.Item>
          <Form.Item name="status" label="Status">
            <Select style={{ width: 140 }} options={TASK_STATUSES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <Select style={{ width: 120 }} options={PRIORITIES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
        </Space>
        <NotifyField people={people} />
        <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
        <AppliesToFields options={options} projectCampaignIds={campaignIds || []} />
      </Form>
    </Modal>
  );
}

// Mass Add Tasks: one task per agent -- each its own thread about that
// person -- with that agent as its Applies To and the other fields the same.
// Agents come from a Team (the user's Visible Teams) and one or more Jobs;
// all are ticked, and any can be unticked.
export function MassAddTaskModal({ open, folderId, projectId, options, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [jobIds, setJobIds] = useState([]);
  const [unticked, setUnticked] = useState(() => new Set());
  const people = useFolderPeople(open ? projectId : null);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ status: 'Open', priority: 'Normal', links: [], append_name: true, applies_show_on: [] });
    setTeamId(null); setJobIds([]); setUnticked(new Set());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const agents = teamId && jobIds.length
    ? options.agents.filter((a) => a.TeamID === teamId && jobIds.includes(a.JobID))
    : [];
  const picked = agents.filter((a) => !unticked.has(a.ID));
  const toggle = (id) => setUnticked((cur) => {
    const n = new Set(cur);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    if (!picked.length) { message.warning('Pick a team and job(s), and leave at least one agent ticked.'); return; }
    setSaving(true);
    try {
      const res = await api.post(`/api/projects/${folderId}/tasks/bulk`, {
        folder_id: projectId, title: v.title, notes: v.notes || '', assigned_to: v.assigned_to || [],
        due_date: v.due_date ? v.due_date.format('YYYY-MM-DD') : null, status: v.status, priority: v.priority,
        repeat: v.repeat || null,
        links: linksFromForm(v.links), notify_when_done: v.notify_when_done || [],
        applies_show_on: v.applies_show_on || [],
        applies_from: v.applies_dates?.[0]?.format('YYYY-MM-DD') || null,
        applies_until: v.applies_dates?.[1]?.format('YYYY-MM-DD') || null,
        agent_ids: picked.map((a) => a.ID), append_name: !!v.append_name,
      });
      message.success(`Added ${res.data.created.length} tasks.`);
      onSaved?.();
      onClose();
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
      onSaved?.();   // some may have been added before a failure
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal maskClosable={false} title="Mass Add Tasks" open={open} onOk={save} onCancel={onClose}
      confirmLoading={saving} okText={`Add ${picked.length} task${picked.length === 1 ? '' : 's'}`} width={760} destroyOnClose>
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Card size="small" title="Agents (one task each)" style={{ marginBottom: 16 }}>
          <Space wrap size={12} style={{ marginBottom: 8 }}>
            <Select showSearch filterOption={selectFilter} placeholder="Team" style={{ width: 220 }} value={teamId}
              onChange={(v) => { setTeamId(v); setUnticked(new Set()); }}
              options={options.teams.map((t) => ({ value: t.ID, label: t.Name }))} />
            <Select mode="multiple" showSearch filterOption={selectFilter} placeholder="Job(s)" style={{ minWidth: 260 }}
              value={jobIds} onChange={(v) => { setJobIds(v); setUnticked(new Set()); }}
              options={options.jobs.map((j) => ({ value: j.JobID, label: j.JobName }))} />
          </Space>
          {teamId && jobIds.length > 0 && (agents.length ? (
            <>
              <div style={{ marginBottom: 6 }}>
                <Typography.Text type="secondary">{picked.length} of {agents.length} ticked · </Typography.Text>
                <Typography.Link onClick={() => setUnticked(new Set())}>Tick all</Typography.Link>
                <Typography.Text type="secondary"> · </Typography.Text>
                <Typography.Link onClick={() => setUnticked(new Set(agents.map((a) => a.ID)))}>Untick all</Typography.Link>
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4 }}>
                {agents.map((a) => (
                  <Checkbox key={a.ID} checked={!unticked.has(a.ID)} onChange={() => toggle(a.ID)}>{a.DisplayName}</Checkbox>
                ))}
              </div>
            </>
          ) : <Typography.Text type="secondary">No active agents on that team with those jobs.</Typography.Text>)}
        </Card>
        <Form.Item name="title" label="Task" rules={[{ required: true, message: 'A task needs a title' }]}><Input maxLength={250} /></Form.Item>
        <Form.Item name="append_name" valuePropName="checked" style={{ marginTop: -16 }}>
          <Checkbox>Add each agent&apos;s name to the title (e.g. &quot;… - Jane Doe&quot;)</Checkbox>
        </Form.Item>
        <Form.Item name="notes" label="Notes"><Input.TextArea autoSize={{ minRows: 2, maxRows: 10 }} /></Form.Item>
        <Space wrap size={12}>
          <Form.Item name="assigned_to" label="Assigned To">
            <Select mode="multiple" maxCount={MAX_ASSIGNEES} allowClear showSearch filterOption={selectFilter}
              style={{ minWidth: 240, maxWidth: 480 }} placeholder="Unassigned" options={assigneeOptions(options.users)} />
          </Form.Item>
          <Form.Item name="due_date" label="Due"><DatePicker /></Form.Item>
          <Form.Item name="repeat" label="Repeat"><Select allowClear placeholder="Does not repeat" style={{ width: 160 }} options={REPEAT_OPTIONS} /></Form.Item>
          <Form.Item name="status" label="Status">
            <Select style={{ width: 140 }} options={TASK_STATUSES.map((st) => ({ value: st, label: st }))} />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <Select style={{ width: 120 }} options={PRIORITIES.map((st) => ({ value: st, label: st }))} />
          </Form.Item>
        </Space>
        <NotifyField people={people} />
        <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
        <Card size="small" title="Applies To (each task: its agent)">
          <Space wrap size={24} align="start">
            <Form.Item name="applies_show_on" label="Show on"><Checkbox.Group options={SHOW_ON_OPTIONS} /></Form.Item>
            <Form.Item name="applies_dates" label="Dates" style={{ marginBottom: 0 }}>
              <DatePicker.RangePicker allowEmpty={[true, true]} placeholder={['From', 'Until']} />
            </Form.Item>
          </Space>
        </Card>
      </Form>
    </Modal>
  );
}

// Comment text with its web addresses clickable (opening in a new tab).
function Linkified({ text }) {
  const parts = String(text || '').split(/(https?:\/\/[^\s<>"']+)/g);
  return parts.map((p, i) => (/^https?:\/\//.test(p)
    ? <a key={i} href={p} target="_blank" rel="noopener noreferrer">{p}</a>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

// The Type select for a Folder or Project: active types, plus the current one
// if it has since been made inactive.
export const typeOptions = (options, currentId, currentName) => [
  ...options.types.map((t) => ({ value: t.ID, label: t.Name })),
  ...(currentId && !options.types.some((t) => t.ID === currentId)
    ? [{ value: currentId, label: `${currentName || `#${currentId}`} (inactive)` }] : []),
];

// A person in a picker: "Name (Team)", as on the other user pickers.
export const userLabel = (u) => (u.TeamName ? `${u.DisplayName} (${u.TeamName})` : u.DisplayName);
// Task Assigned To: one or several people (any of them can mark it Done),
// up to MAX_ASSIGNEES. No View Only users, except whoever the task already has.
const MAX_ASSIGNEES = 10;
const assigneeOptions = (users, keepIds = []) => users.filter((u) => !u.IsViewOnly || keepIds.includes(u.ID))
  .map((u) => ({ value: u.ID, label: userLabel(u) }));

// Pickers (users, teams, jobs, campaigns) and whether the user may create.
export function useProjectOptions() {
  const [opts, setOpts] = useState({ users: [], agents: [], teams: [], jobs: [], campaigns: [], types: [], canCreate: false });
  useEffect(() => {
    api.get('/api/projects/options').then((r) => setOpts(r.data || opts)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return opts;
}

export function LinksView({ links }) {
  if (!links?.length) return null;
  return (
    <Space direction="vertical" size={2}>
      {links.map((l) => (
        <a key={l.ID ?? l.URL} href={l.URL} target="_blank" rel="noopener noreferrer">
          <LinkOutlined /> {l.Label || l.URL}
        </a>
      ))}
    </Space>
  );
}

export const linksToForm = (links) => (links || []).map((l) => ({ url: l.URL, label: l.Label || '' }));
export const linksFromForm = (v) => cleanLinks(v);

// The pickers list only what the user may pick (their Visible Teams and
// Campaigns). A row someone with wider scope added comes back locked: shown
// by name, not editable or removable here, and kept by the server whatever
// is sent. A row whose item is simply not in the list (e.g. a person since
// made inactive) keeps its name in the picker.
const withCurrent = (opts, v) => (v?.id == null || opts.some((o) => o.value === v.id)
  ? opts : [{ value: v.id, label: v.name || `#${v.id}` }, ...opts]);
const LockedRow = ({ children }) => (
  <Tag icon={<LockOutlined />} title="Added by someone with access to more teams or campaigns">{children}</Tag>
);

// A Folder's or Project's Campaigns select: the user's campaigns plus those
// already on it; ones outside their Visible Campaigns cannot be removed.
export const campaignOptions = (options, current = []) => [
  ...options.campaigns.map((c) => ({ value: c.ID, label: c.Name })),
  ...current.filter((c) => !options.campaigns.some((o) => o.ID === c.ID))
    .map((c) => ({ value: c.ID, label: c.Name, disabled: !!c.Locked })),
];

// Members: a person or a job (someone in by job sees it only with one of its
// campaigns -- routes/projects _job_member_ok); Member or Manager on a
// project (a folder's members are always Members).
const TYPE_OPTIONS = [{ value: 'user', label: 'Person' }, { value: 'job', label: 'Job' }];
export function MembersFormList({ name = 'members', options, withRole = true }) {
  const pick = {
    user: options.users.map((u) => ({ value: u.ID, label: userLabel(u) })),
    job:  options.jobs.map((j) => ({ value: j.JobID, label: j.JobName })),
  };
  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          {fields.map(({ key, name: row }) => (
            <Form.Item key={key} noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const v = getFieldValue([name, row]) || {};
                if (v.locked) {
                  return (
                    <div>
                      <LockedRow>{MEMBER_PREFIX[v.type]}{v.name}{withRole && v.role === 'Manager' ? ' · Manager' : ''}</LockedRow>
                    </div>
                  );
                }
                return (
                  <Space align="baseline" wrap>
                    <Form.Item name={[row, 'type']} noStyle initialValue="user">
                      <Select style={{ width: 100 }} options={TYPE_OPTIONS} />
                    </Form.Item>
                    <Form.Item name={[row, 'id']} noStyle rules={[{ required: true, message: 'Pick one' }]}>
                      <Select showSearch filterOption={selectFilter} style={{ width: 260 }} placeholder="Pick…"
                        options={withCurrent(pick[v.type || 'user'], v)} />
                    </Form.Item>
                    {withRole && (
                      <Form.Item name={[row, 'role']} noStyle initialValue="Member">
                        <Select style={{ width: 120 }} options={[{ value: 'Member', label: 'Member' }, { value: 'Manager', label: 'Manager' }]} />
                      </Form.Item>
                    )}
                    <MinusCircleOutlined onClick={() => remove(row)} style={{ color: '#999' }} />
                  </Space>
                );
              }}
            </Form.Item>
          ))}
          <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: 'user', role: 'Member' })}>Add Member</Button>
        </Space>
      )}
    </Form.List>
  );
}

const MEMBER_PREFIX = { user: '', team: 'Team: ', job: 'Job: ' };
export const membersToForm = (members) => (members || []).map((m) => ({
  type: m.type, id: m.id, role: m.role, name: m.name, locked: !!m.locked,
}));

export function MembersView({ members }) {
  if (!members?.length) return <Typography.Text type="secondary">—</Typography.Text>;
  const label = { user: '', team: 'Team: ', job: 'Job: ' };
  return (
    <Space size={[4, 4]} wrap>
      {members.map((m) => (
        <Tag key={`${m.type}${m.id}`} color={m.role === 'Manager' ? 'gold' : undefined}>
          {label[m.type]}{m.name}{m.role === 'Manager' ? ' · Manager' : ''}
        </Tag>
      ))}
    </Space>
  );
}

// Applies To on a task: who it is about (agents, teams, campaigns, jobs), the
// pages its FYI shows on, and an optional date window. It never gives access.
// 'agent-summary' is still accepted by the server but no longer offered.
export const SHOW_ON_OPTIONS = [
  { value: 'evaluations', label: 'Evaluations' },
  { value: 'errors', label: 'Errors' },
  { value: 'coaching', label: 'Coaching' },
];
const SHOW_ON_LABEL = { evaluations: 'Evaluations', errors: 'Errors', coaching: 'Coaching', 'agent-summary': 'Agent Summary' };
const APPLIES_TYPE_OPTIONS = [
  { value: 'agent', label: 'Agent' }, { value: 'team', label: 'Team' },
  { value: 'campaign', label: 'Campaign' }, { value: 'job', label: 'Job' },
];
const APPLIES_PREFIX = { agent: '', team: 'Team: ', campaign: 'Campaign: ', job: 'Job: ' };

// projectCampaignIds: the task's campaign scope (its Project's campaigns, or
// if none its Folder's); when there are any, the Campaign picker offers only
// those.
export function AppliesToFields({ options, projectCampaignIds = [] }) {
  const pick = {
    agent: options.agents.map((u) => ({ value: u.ID, label: userLabel(u) })),
    team: options.teams.map((t) => ({ value: t.ID, label: t.Name })),
    campaign: options.campaigns
      .filter((c) => !projectCampaignIds.length || projectCampaignIds.includes(c.ID))
      .map((c) => ({ value: c.ID, label: c.Name })),
    job: options.jobs.map((j) => ({ value: j.JobID, label: j.JobName })),
  };
  return (
    // One box: who, where and when belong together.
    <Card size="small" title="Applies To" style={{ marginBottom: 16 }}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Optional. Use this when the task is about specific agents, for example a client asking QA to monitor
        them. Members of this folder or project will see an FYI about the task on the pages you tick, when
        they open one for these agents (or anyone in these teams, campaigns or jobs) between these dates.
        The agents never see it, and it does not give anyone access to the task.
      </Typography.Paragraph>
      <Form.Item label="Who">
        <Form.List name="applies_to">
          {(fields, { add, remove }) => (
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              {fields.map(({ key, name: row }) => (
                <Form.Item key={key} noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const v = getFieldValue(['applies_to', row]) || {};
                    if (v.locked) return <div><LockedRow>{APPLIES_PREFIX[v.type]}{v.name}</LockedRow></div>;
                    return (
                      <Space align="baseline" wrap>
                        <Form.Item name={[row, 'type']} noStyle initialValue="agent">
                          <Select style={{ width: 120 }} options={APPLIES_TYPE_OPTIONS} />
                        </Form.Item>
                        <Form.Item name={[row, 'id']} noStyle rules={[{ required: true, message: 'Pick one' }]}>
                          <Select showSearch filterOption={selectFilter} style={{ width: 300 }} placeholder="Pick…"
                            options={withCurrent(pick[v.type || 'agent'], v)} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(row)} style={{ color: '#999' }} />
                      </Space>
                    );
                  }}
                </Form.Item>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ type: 'agent' })}>Add</Button>
            </Space>
          )}
        </Form.List>
      </Form.Item>
      <Space wrap size={24} align="start">
        {/* Who alone is fine (a to-do about someone); Show on needs a Who. */}
        <Form.Item name="applies_show_on" label="Show on" dependencies={['applies_to']}
          rules={[({ getFieldValue }) => ({
            validator: (_, v) => (v?.length && !(getFieldValue('applies_to') || []).some((a) => a?.id != null)
              ? Promise.reject(new Error('Pick who it applies to before choosing pages to show it on'))
              : Promise.resolve()),
          })]}>
          <Checkbox.Group options={SHOW_ON_OPTIONS} />
        </Form.Item>
        <Form.Item name="applies_dates" label="Dates" style={{ marginBottom: 0 }}>
          <DatePicker.RangePicker allowEmpty={[true, true]} placeholder={['From', 'Until']} />
        </Form.Item>
      </Space>
    </Card>
  );
}

export const appliesToForm = (t) => ({
  applies_to: (t?.AppliesTo || []).map((a) => ({ type: a.type, id: a.id, name: a.name, locked: !!a.locked })),
  applies_show_on: t?.AppliesShowOn || [],
  applies_dates: [t?.AppliesFrom ? dayjs(t.AppliesFrom) : null, t?.AppliesUntil ? dayjs(t.AppliesUntil) : null],
});
export const appliesFromForm = (v) => ({
  applies_to: (v.applies_to || []).filter((a) => a && a.id != null),
  applies_show_on: v.applies_show_on || [],
  applies_from: v.applies_dates?.[0]?.format('YYYY-MM-DD') || null,
  applies_until: v.applies_dates?.[1]?.format('YYYY-MM-DD') || null,
});

// Tags for the Applies To targets; compact = at most `max`, then "+n".
export function AppliesToTags({ items, max }) {
  if (!items?.length) return <Typography.Text type="secondary">—</Typography.Text>;
  const shown = max ? items.slice(0, max) : items;
  return (
    <Space size={[4, 4]} wrap>
      {shown.map((a) => <Tag key={`${a.type}${a.id}`}>{APPLIES_PREFIX[a.type]}{a.name}</Tag>)}
      {items.length > shown.length && <Tag>+{items.length - shown.length}</Tag>}
    </Space>
  );
}

// The Applies To column: who (up to 3 tags), the dates and the Show On pages.
export function AppliesToCell({ task }) {
  const who = task.AppliesTo || [];
  const show = task.AppliesShowOn || [];
  if (!who.length && !show.length && !task.AppliesFrom && !task.AppliesUntil) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }
  return (
    <div>
      {who.length > 0 && <AppliesToTags items={who} max={3} />}
      {(task.AppliesFrom || task.AppliesUntil) && (
        <div style={{ fontSize: 12, marginTop: 2 }}>{task.AppliesFrom || '…'} – {task.AppliesUntil || '…'}</div>
      )}
      {show.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>Show On: </Typography.Text>
          {show.map((k) => SHOW_ON_LABEL[k] || k).join(', ')}
        </div>
      )}
    </div>
  );
}

export function AppliesToView({ task }) {
  const has = task.AppliesTo?.length || task.AppliesShowOn?.length || task.AppliesFrom || task.AppliesUntil;
  if (!has) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <Typography.Text strong>Applies To</Typography.Text>
      <div style={{ marginTop: 4 }}><AppliesToTags items={task.AppliesTo} /></div>
      {task.AppliesShowOn?.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <Typography.Text type="secondary">Show on </Typography.Text>
          {task.AppliesShowOn.map((k) => SHOW_ON_LABEL[k] || k).join(', ')}
        </div>
      )}
      {(task.AppliesFrom || task.AppliesUntil) && (
        <div style={{ marginTop: 4 }}>
          <Typography.Text type="secondary">Dates </Typography.Text>
          {task.AppliesFrom || '…'} – {task.AppliesUntil || '…'}
        </div>
      )}
    </div>
  );
}

// The FYI on a record page (evaluations, errors, coaching): open project
// tasks about the record's agent -- or, on a coaching session, any of its
// participants (agentUserIds; each task then says who it is about) -- that
// the viewer is on. Nothing at all when there are none, or for those without
// Projects Access. Never about the viewer themself.
const RECORD_LABEL = { evaluation: 'Evaluation', error: 'Error', coaching: 'Coaching Session' };
const RECORD_PATH = { evaluation: '/cases/', error: '/errors/', coaching: '/coaching/sessions/' };

// recordType / recordId: the record on the page, which each task can be
// linked to (shown on the task as Linked Records). A task's title opens it in
// the task panel, so the page is not left.
export function ProjectFyi({ page, agentUserId, agentUserIds, campaignId, currentUser, recordType, recordId }) {
  const [rows, setRows] = useState([]);
  const [taskId, setTaskId] = useState(null);
  const [busy, setBusy] = useState(null);
  const ids = (agentUserIds || (agentUserId ? [agentUserId] : [])).filter((i) => i && i !== currentUser?.ID);
  const idsKey = ids.join(',');
  const many = !!agentUserIds;
  const allowed = ids.length > 0 && currentUser?.Role !== 'agent'
    && (currentUser?.Role === 'admin' || (currentUser?.CanEditProjects ?? 0) >= 1);
  const load = useCallback(() => {
    if (!allowed) { setRows([]); return; }
    api.get('/api/project-fyi', { params: {
      page, agent_user_ids: idsKey, ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(recordType && recordId ? { record_type: recordType, record_id: recordId } : {}),
    } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [allowed, page, idsKey, campaignId, recordType, recordId]);
  useEffect(() => { load(); }, [load]);
  // Link: a pop-up to add a comment on the task as well, pre-filled with the
  // record and its address; "Link and Add Comment" does both.
  const [linking, setLinking] = useState(null);   // the task being linked to
  const [linkNote, setLinkNote] = useState('');
  const recordUrl = `${window.location.origin}${RECORD_PATH[recordType] || '/'}${recordId}`;
  const openLink = (t) => {
    setLinkNote(`${RECORD_LABEL[recordType] || 'Record'} #${recordId}: ${recordUrl}

`);
    setLinking(t);
  };
  // withComment false: Link Only.
  const linkAndComment = async (withComment = true) => {
    const t = linking;
    setBusy(t.ID);
    try {
      await api.post(`/api/project-tasks/${t.ID}/links`, { record_type: recordType, record_id: recordId });
      if (withComment && linkNote.trim()) await api.post('/api/project-comments', { task_id: t.ID, comment: linkNote.trim() });
      message.success(withComment ? 'Linked, and the comment added to the task.' : 'Linked.');
      setLinking(null);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not link.');
      load();
    } finally {
      setBusy(null);
    }
  };
  const link = (t) => {
    setBusy(t.ID);
    (t.LinkID
      ? api.delete(`/api/project-task-links/${t.LinkID}`)
      : api.post(`/api/project-tasks/${t.ID}/links`, { record_type: recordType, record_id: recordId }))
      .then(load)
      .catch((err) => message.error(err.response?.data?.error || 'Could not change the link.'))
      .finally(() => setBusy(null));
  };
  if (!rows.length) return null;
  // Collapsed by default with a red header, like Active Compliance QA Related.
  return (
    <>
    <Collapse
      className="no-print attention-collapse"
      defaultActiveKey={[]}
      style={{ marginBottom: 16 }}
      items={[{
        key: 'project-fyi',
        label: (
          <span>
            <InfoCircleOutlined style={{ marginRight: 6 }} />Project FYI ({rows.length})
            <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
              click to expand / collapse
            </Typography.Text>
          </span>
        ),
        children: (
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        {rows.map((t) => (
          <div key={t.ID}>
            <div>
              <a href={`/folders/${t.ProjectID}`} target="_blank" rel="noopener noreferrer">{t.ProjectName}</a>
              <Typography.Text type="secondary"> › </Typography.Text>
              <a href={projectPath(t.ProjectID, t.FolderID)} target="_blank" rel="noopener noreferrer">{t.FolderName}</a>
              <Typography.Text type="secondary"> › </Typography.Text>
              <Typography.Link strong onClick={() => setTaskId(t.ID)}>{t.Title}</Typography.Link>
              {/* Plain text, not a tag: a grey "Open" tag beside the links read
                  as a button to open the task. */}
              <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>Status: {t.Status}</Typography.Text>
              {recordType && recordId && (t.LinkID ? (
                <Space size={4} style={{ marginLeft: 4 }}>
                  <Tag color="green" icon={<LinkOutlined />}>Linked</Tag>
                  <Button size="small" type="link" loading={busy === t.ID} onClick={() => link(t)}>Unlink</Button>
                </Space>
              ) : (
                <Button size="small" icon={<LinkOutlined />} loading={busy === t.ID} onClick={() => openLink(t)} style={{ marginLeft: 4 }}>
                  Link this {RECORD_LABEL[recordType].toLowerCase()}
                </Button>
              ))}
            </div>
            {many && t.About?.length > 0 && (
              <div style={{ marginTop: 2 }}>
                <Typography.Text type="secondary">About: </Typography.Text>{t.About.join(', ')}
              </div>
            )}
            {t.Notes && <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{t.Notes}</div>}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {[
                (t.AppliesFrom || t.AppliesUntil) && `${t.AppliesFrom || '…'} – ${t.AppliesUntil || '…'}`,
                t.AssignedToName && `Assigned to ${t.AssignedToName}`,
              ].filter(Boolean).join(' · ')}
            </Typography.Text>
          </div>
        ))}
      </Space>
        ),
      }]}
    />
    {taskId && <FyiTaskDrawer taskId={taskId} onClose={() => setTaskId(null)} onChanged={load} />}
    <Modal maskClosable={false} open={!!linking} title={linking ? `Link to task: ${linking.Title}` : ''}
      onCancel={() => setLinking(null)} width={640} destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => setLinking(null)}>Cancel</Button>,
        <Button key="only" loading={!!linking && busy === linking.ID} onClick={() => linkAndComment(false)}>Link Only</Button>,
        <Button key="ok" type="primary" icon={<LinkOutlined />} loading={!!linking && busy === linking.ID}
          onClick={() => linkAndComment(true)}>Link and Add Comment</Button>,
      ]}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Comment on the task (it keeps the link to this {RECORD_LABEL[recordType]?.toLowerCase()}):
      </Typography.Paragraph>
      <Input.TextArea autoSize={{ minRows: 4, maxRows: 12 }} maxLength={4000} value={linkNote}
        onChange={(e) => setLinkNote(e.target.value)} autoFocus />
    </Modal>
    </>
  );
}

// Create Task on a record page (a coaching session): pick one of your
// projects -- active, not archived, and one you can add tasks to (not "Task
// only") -- then the usual Add Task form. Its Applies To starts as the
// record's agents (those you can pick) with Show on this page, so the task
// lists in the page's Project FYI; the record is linked to the new task.
export function CreateTaskFromRecord({ open, recordType, recordId, agentUserIds, page, onClose, onCreated }) {
  const [projects, setProjects] = useState(null);
  const [picked, setPicked] = useState(null);
  const [adding, setAdding] = useState(false);
  const options = useProjectOptions();
  useEffect(() => {
    if (!open) return;
    setPicked(null);
    setAdding(false);
    setProjects(null);
    api.get('/api/project-folders')
      .then((r) => setProjects((r.data || []).filter((f) => f.MyAccess !== 'Task' && f.ProjectStatus === 'Active')))
      .catch(() => setProjects([]));
  }, [open]);
  const project = (projects || []).find((f) => f.ID === picked);
  const agents = options.agents.filter((a) => (agentUserIds || []).includes(a.ID));
  const initial = {
    applies_to: agents.map((a) => ({ type: 'agent', id: a.ID, name: userLabel(a), locked: false })),
    applies_show_on: agents.length ? [page] : [],
  };
  const saved = async (taskId) => {
    if (taskId) {
      try {
        await api.post(`/api/project-tasks/${taskId}/links`, { record_type: recordType, record_id: recordId });
      } catch {
        message.warning(`The task was added, but this ${RECORD_LABEL[recordType]?.toLowerCase() || 'record'} could not be linked to it.`);
      }
    }
    onCreated?.();
  };
  return (
    <>
      <Modal maskClosable={false} title="Create Task" open={open && !adding} onCancel={onClose}
        okText="Next" okButtonProps={{ disabled: !project }} onOk={() => setAdding(true)} destroyOnClose>
        {projects === null ? <Spin /> : (
          <Form layout="vertical" style={{ marginTop: 12 }}>
            <Form.Item label="Project" extra={projects.length ? null : 'You are not a member of any active project.'}>
              <Select showSearch filterOption={selectFilter} placeholder="Pick a project" value={picked}
                onChange={setPicked} disabled={!projects.length}
                options={projects.map((f) => ({ value: f.ID, label: `${f.ProjectName} › ${f.Name}` }))} />
            </Form.Item>
          </Form>
        )}
      </Modal>
      {project && (
        <AddTaskModal open={open && adding} folderId={project.ProjectID} projectId={project.ID}
          campaignIds={(project.Campaigns || []).map((c) => c.ID)} options={options} initial={initial}
          onClose={onClose} onSaved={saved} />
      )}
    </>
  );
}

// The task panel opened from an FYI; its pickers load only once it is opened.
function FyiTaskDrawer({ taskId, onClose, onChanged }) {
  const options = useProjectOptions();
  return <TaskDrawer open taskId={taskId} onClose={onClose} onChanged={onChanged} options={options} />;
}

// Linked Records on a task: evaluations, errors and coaching sessions linked
// from their pages' Project FYI, as a sortable table (Agent, then Record Date
// newest first). Details only for records the viewer can see.
export function TaskLinks({ taskId }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get(`/api/project-tasks/${taskId}/links`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [taskId]);
  useEffect(() => { load(); }, [load]);
  const unlink = (id) => api.delete(`/api/project-task-links/${id}`).then(load)
    .catch((err) => message.error(err.response?.data?.error || 'Could not unlink.'));
  if (rows === null) return <Spin size="small" />;
  if (!rows.length) return <Typography.Text type="secondary" style={{ fontSize: 12 }}>None linked yet.</Typography.Text>;
  // Agent A-Z, then the record's date newest first; every column sorts.
  const byText = (k) => (a, b) => (a[k] || '').localeCompare(b[k] || '');
  const sorted = [...rows].sort((a, b) => (a.Agent || '~').localeCompare(b.Agent || '~')
    || (b.RecordDate || '').localeCompare(a.RecordDate || ''));
  return (
    <Table rowKey="ID" size="small" pagination={false} dataSource={sorted} scroll={{ x: 'max-content' }}
      columns={[
        { title: 'Type', dataIndex: 'RecordType', key: 'RecordType', width: 140, sorter: byText('RecordType'),
          render: (v) => <Tag>{RECORD_LABEL[v] || v}</Tag> },
        { title: 'Record', key: 'Record', width: 220, sorter: (a, b) => a.RecordID - b.RecordID,
          render: (_, l) => (
            <>
              {l.CanView
                ? <a href={`${RECORD_PATH[l.RecordType]}${l.RecordID}`} target="_blank" rel="noopener noreferrer">#{l.RecordID}</a>
                : <span>#{l.RecordID}</span>}
              {l.Title && <span style={{ marginLeft: 6 }}>{l.Title}</span>}
              {!l.Exists && <Typography.Text type="secondary" style={{ marginLeft: 6 }}>(deleted)</Typography.Text>}
            </>
          ) },
        { title: 'Agent', dataIndex: 'Agent', key: 'Agent', width: 180, sorter: byText('Agent'),
          render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
        { title: 'Record Date', dataIndex: 'RecordDate', key: 'RecordDate', width: 120, sorter: byText('RecordDate'),
          render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
        { title: 'Linked By', dataIndex: 'CreatedByName', key: 'CreatedByName', width: 150, sorter: byText('CreatedByName'),
          render: (v) => v || '—' },
        { title: 'Linked On', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 170, sorter: byText('CreatedAt'),
          render: (v) => formatTS(v) },
        { title: '', key: 'unlink', width: 80, align: 'right',
          render: (_, l) => l.CanUnlink && (
            <Popconfirm title="Unlink this record?" okText="Unlink" onConfirm={() => unlink(l.ID)}>
              <Button size="small" type="link">Unlink</Button>
            </Popconfirm>
          ) },
      ]} />
  );
}

// Comments on a project (projectId), folder (folderId) or task (taskId).
// withTasks (a Project's page): its own comments and its tasks', with a
// Project Level / Task Comments / All toggle and a label on each saying which
// it belongs to; onOpenTask(id) opens a task from its label. New comments
// added here are always Project Level.
// peopleFolderId: the Project (code: folder) whose people can be @mentioned
// -- members of it and of its Folder, and the owner.
export function ProjectComments({ projectId, folderId, taskId, withTasks = false, onOpenTask, peopleFolderId }) {
  const people = useFolderPeople(peopleFolderId || folderId || null);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState('all');
  const target = taskId ? { task_id: taskId } : folderId ? { folder_id: folderId } : { project_id: projectId };
  const key = JSON.stringify(target);
  const load = useCallback(() => {
    api.get('/api/project-comments', { params: { ...target, ...(withTasks ? { with_tasks: 1 } : {}) } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [key, withTasks]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (rows || []).filter((c) => !withTasks || scope === 'all'
    || (scope === 'project' ? !c.TaskID : !!c.TaskID));
  useEffect(() => { load(); }, [load]);
  // The same comment box as the other pages: pasted or attached files are
  // uploaded onto the comment once it is posted.
  const add = async (text, files, mentioned = []) => {
    setSaving(true);
    try {
      const res = await api.post('/api/project-comments', { ...target, comment: text, mention_user_ids: mentioned });
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'project');
        fd.append('comment_id', String(res.data.id));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not add the comment.');
    } finally {
      setSaving(false);
      load();
    }
  };
  return (
    <div>
      {withTasks && (
        <Segmented style={{ marginBottom: 8 }} value={scope} onChange={setScope}
          options={[{ value: 'project', label: 'Project Level' }, { value: 'tasks', label: 'Task Comments' }, { value: 'all', label: 'All' }]} />
      )}
      {rows === null ? <Spin size="small" /> : !shown.length
        ? <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>No comments yet.</Typography.Text></div>
        : shown.map((c) => (
          <div key={c.ID} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
            {withTasks && (c.TaskID
              ? <Tag color="blue" style={{ cursor: onOpenTask ? 'pointer' : undefined }}
                  onClick={() => onOpenTask?.(c.TaskID)}>Task: {c.TaskTitle || `#${c.TaskID}`}</Tag>
              : <Tag color="geekblue">Project Level</Tag>)}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{c.UserName} · {formatTS(c.CreatedAt)}</Typography.Text>
            <div style={{ whiteSpace: 'pre-wrap' }}><Linkified text={c.Comment} /></div>
            <CommentAttachments attachments={c.Attachments} />
          </div>
        ))}
      {/* Task comments are added on the task itself. */}
      {scope !== 'tasks' && (
        <div style={{ marginTop: 8 }}>
          <CommentBox onSubmit={add} loading={saving}
            mentionPeople={people.map((u) => ({ id: u.ID, name: u.DisplayName }))}
            {...(withTasks ? { submitLabel: 'Add Project Comment', placeholder: 'Add a project comment… (@ to notify someone)' }
              : { placeholder: 'Add a comment… (@ to notify someone)' })} />
        </div>
      )}
    </div>
  );
}

// A task: details and edit (for those who see its folder), status only (for
// an assignee who does not), comments. onChanged() after a save or delete.
export function TaskDrawer({ taskId, open, onClose, onChanged, options }) {
  const [task, setTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const people = useFolderPeople(task?.CanEdit ? task.FolderID : null);
  useEffect(() => {
    if (!open || !taskId) { setTask(null); return; }
    api.get(`/api/project-tasks/${taskId}`)
      .then((r) => {
        setTask(r.data);
        form.setFieldsValue({
          title: r.data.Title, notes: r.data.Notes || '', assigned_to: r.data.AssignedToUserIDs || [],
          due_date: r.data.DueDate ? dayjs(r.data.DueDate) : null, status: r.data.Status, priority: r.data.Priority,
          repeat: r.data.Repeat ?? null,
          links: linksToForm(r.data.Links), ...appliesToForm(r.data),
          notify_when_done: (r.data.NotifyWhenDone || []).map((u) => u.ID),
        });
      })
      .catch(() => { message.error('Could not load the task.'); onClose(); });
  }, [open, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    const body = task.CanEdit ? {
      title: v.title, notes: v.notes || '', assigned_to: v.assigned_to || [],
      due_date: v.due_date ? v.due_date.format('YYYY-MM-DD') : null, status: v.status, priority: v.priority,
      repeat: v.repeat || null,
      links: linksFromForm(v.links), ...appliesFromForm(v), notify_when_done: v.notify_when_done || [],
    } : { status: v.status };
    api.put(`/api/project-tasks/${taskId}`, body)
      .then((res) => {
        message.success('Task saved.');
        const nxt = res.data?.next_task;
        if (nxt) message.info(`Next one created, due ${dayjs(nxt.DueDate).format('ddd MM/DD')}.`);
        onChanged?.(); onClose();
      })
      .catch((err) => message.error(err.response?.data?.error || 'Save failed.'))
      .finally(() => setSaving(false));
  };
  const remove = () => {
    api.delete(`/api/project-tasks/${taskId}`)
      .then(() => { message.success('Task deleted.'); onChanged?.(); onClose(); })
      .catch((err) => message.error(err.response?.data?.error || 'Delete failed.'));
  };

  return (
    <Drawer open={open} onClose={onClose} width="min(1100px, 95vw)" destroyOnClose
      title={task ? `${task.ProjectName} › ${task.FolderName}` : 'Task'}
      extra={task && (
        <Space>
          {task.CanDelete && (
            <Popconfirm title="Delete this task?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={remove}>
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
          {(task.CanEdit || task.IsAssignee) && <Button type="primary" loading={saving} onClick={save}>Save</Button>}
        </Space>
      )}>
      {!task ? <Spin /> : (
        <>
          <Form form={form} layout="vertical">
            {task.CanEdit ? (
              <>
                <Form.Item name="title" label="Task" rules={[{ required: true, message: 'A task needs a title' }]}>
                  <Input maxLength={300} />
                </Form.Item>
                <Form.Item name="notes" label="Notes"><Input.TextArea autoSize={{ minRows: 3, maxRows: 12 }} /></Form.Item>
                <Space wrap size={12}>
                  <Form.Item name="assigned_to" label="Assigned To">
                    <Select mode="multiple" maxCount={MAX_ASSIGNEES} allowClear showSearch filterOption={selectFilter}
                      style={{ minWidth: 240, maxWidth: 480 }} placeholder="Unassigned"
                      options={assigneeOptions(options.users, task?.AssignedToUserIDs || [])} />
                  </Form.Item>
                  <Form.Item name="due_date" label="Due"><DatePicker /></Form.Item>
                  <Form.Item name="repeat" label="Repeat"><Select allowClear placeholder="Does not repeat" style={{ width: 160 }} options={REPEAT_OPTIONS} /></Form.Item>
                  <Form.Item name="status" label="Status">
                    <Select style={{ width: 140 }} options={TASK_STATUSES.map((s) => ({ value: s, label: s }))} />
                  </Form.Item>
                  <Form.Item name="priority" label="Priority">
                    <Select style={{ width: 120 }} options={PRIORITIES.map((s) => ({ value: s, label: s }))} />
                  </Form.Item>
                </Space>
                <NotifyField people={people} current={task.NotifyWhenDone || []} />
                <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
                <AppliesToFields options={options} projectCampaignIds={task.ProjectCampaignIDs} />
              </>
            ) : (
              <>
                <Typography.Title level={5} style={{ marginTop: 0 }}>{task.Title}</Typography.Title>
                {task.Notes && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>{task.Notes}</div>}
                <div style={{ marginBottom: 12 }}>
                  <Typography.Text type="secondary">Due </Typography.Text>{task.DueDate || '—'}
                  <Typography.Text type="secondary" style={{ marginLeft: 16 }}>Priority </Typography.Text>
                  <Tag color={PRIORITY_COLOR[task.Priority]}>{task.Priority}</Tag>
                </div>
                <LinksView links={task.Links} />
                {task.NotifyWhenDone?.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <Typography.Text type="secondary">Notify When Done: </Typography.Text>
                    {task.NotifyWhenDone.map((u) => u.DisplayName).join(', ')}
                  </div>
                )}
                <div style={{ marginTop: 12 }}><AppliesToView task={task} /></div>
                {task.IsAssignee && (
                  <Form.Item name="status" label="Status" style={{ marginTop: 12 }}>
                    <Select style={{ width: 160 }} options={TASK_STATUSES.map((s) => ({ value: s, label: s }))} />
                  </Form.Item>
                )}
              </>
            )}
          </Form>
          <Typography.Title level={5}>Linked Records</Typography.Title>
          <div style={{ marginBottom: 16 }}><TaskLinks taskId={task.ID} /></div>
          <Typography.Title level={5}>Comments</Typography.Title>
          <ProjectComments taskId={task.ID} peopleFolderId={task.FolderID} />
        </>
      )}
    </Drawer>
  );
}
